import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, templatesAPI, tyreSurveyAPI, fuelAPI, planningAPI, replacementAPI } from '../services/api';
import { 
  ClipboardList, 
  Clock, 
  AlertTriangle, 
  CheckCircle2,
  ChevronRight,
  FileText,
  Truck,
  Users,
  TrendingUp,
  Calendar,
  Loader2,
  AlertCircle,
  Wrench,
  Gauge,
  CircleDot,
  Fuel,
  Droplets,
  BarChart3,
  Package,
  Scale,
  X,
  Replace
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import IncidentQuestionnaireBanner from '../components/IncidentQuestionnaireBanner';

const StatCard = ({ icon: Icon, label, value, trend, color = 'slate' }) => {
  const colorClasses = {
    slate: 'bg-slate-100 text-slate-600',
    orange: 'bg-orange-100 text-orange-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    red: 'bg-red-100 text-red-600',
    blue: 'bg-blue-100 text-blue-600',
  };

  return (
    <Card className="card-industrial" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          {trend && (
            <Badge variant="outline" className="text-xs">
              {trend}
            </Badge>
          )}
        </div>
        <div className="mt-4">
          <p className="text-3xl font-bold text-slate-900 font-mono tabular-nums">{value}</p>
          <p className="text-sm font-medium text-slate-500 uppercase tracking-wide mt-1">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const QuickActionButton = ({ icon: Icon, label, onClick }) => (
  <button 
    onClick={onClick}
    className="quick-action-btn"
    data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon className="quick-action-icon" />
    <span className="quick-action-label">{label}</span>
  </button>
);

export const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const envPrefix = location.pathname.startsWith('/test') ? '/test' : '';
  const { user, isDriver, isSupervisor, isManagement } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [fleetExpiryData, setFleetExpiryData] = useState(null);
  const [inspectionIssues, setInspectionIssues] = useState(null);
  const [serviceAlerts, setServiceAlerts] = useState(null);
  const [tyreSurveyStatus, setTyreSurveyStatus] = useState(null);
  const [fuelStats, setFuelStats] = useState(null);
  const [planningStats, setPlanningStats] = useState(null);
  const [replacementStats, setReplacementStats] = useState(null);
  const [showPlanningBanner, setShowPlanningBanner] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch appropriate dashboard based on role
        let dashboardResponse;
        if (isManagement) {
          dashboardResponse = await dashboardAPI.management();
        } else if (isSupervisor) {
          dashboardResponse = await dashboardAPI.supervisor();
        } else {
          dashboardResponse = await dashboardAPI.driver();
        }
        
        setDashboardData(dashboardResponse.data);
        
        // Fetch fleet expiry data and inspection issues for supervisors and management
        if (isSupervisor || isManagement) {
          try {
            const [fleetExpiryResponse, inspectionIssuesResponse, serviceAlertsResponse, tyreSurveyResponse, fuelStatsResponse, planningStatsResponse, replacementStatsResponse] = await Promise.all([
              dashboardAPI.fleetExpiry(),
              dashboardAPI.inspectionIssues(),
              dashboardAPI.serviceAlerts(),
              tyreSurveyAPI.getCurrentMonthStatus(),
              fuelAPI.getStatistics(),  // Get current month stats
              planningAPI.getStatistics(),  // Get current month planning stats
              replacementAPI.getStatistics()  // Get replacement plan stats
            ]);
            setFleetExpiryData(fleetExpiryResponse.data);
            setInspectionIssues(inspectionIssuesResponse.data);
            setServiceAlerts(serviceAlertsResponse.data);
            setTyreSurveyStatus(tyreSurveyResponse.data);
            setFuelStats(fuelStatsResponse.data);
            setPlanningStats(planningStatsResponse.data);
            setReplacementStats(replacementStatsResponse.data);
          } catch (err) {
            console.warn('Failed to fetch dashboard data:', err);
          }
        }
        
        // Fetch templates for quick actions
        const templatesResponse = await templatesAPI.list();
        setTemplates(templatesResponse.data);
        
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isDriver, isSupervisor, isManagement]);

  const handleStartInspection = () => {
    navigate(`${envPrefix}/submissions/new/F004`);
  };

  const handleNewSubmission = (templateCode) => {
    navigate(`${envPrefix}/submissions/new/${templateCode}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-slate-600">{error}</p>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  // Get commonly used templates for quick actions
  const quickTemplates = templates.filter(t => 
    ['F004', 'F010', 'D002', 'D003'].includes(t.code)
  ).slice(0, 4);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            {user?.role === 'supervisor' ? 'SUPERVISOR DASHBOARD' : 
             user?.role === 'management' || user?.role === 'admin' ? 'MANAGEMENT DASHBOARD' : 
             'DRIVER DASHBOARD'}
          </h1>
          <p className="text-slate-500 mt-1">
            Welcome back, {user?.first_name}. Here's your compliance overview.
          </p>
        </div>
        <Button 
          onClick={handleStartInspection}
          className="btn-action"
          data-testid="start-inspection-btn"
        >
          <ClipboardList className="w-5 h-5 mr-2" />
          Start Daily Inspection
        </Button>
      </div>

      {/* Incident Questionnaire Banner */}
      <IncidentQuestionnaireBanner />

      {/* Monthly Fuel Statistics Widget */}
      {(isSupervisor || isManagement) && fuelStats && (
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Fuel className="w-5 h-5 text-amber-500" />
                Fuel Statistics (This Month)
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(`${envPrefix}/fuel-schedule`)}
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <Fuel className="w-6 h-6 mx-auto text-amber-500 mb-2" />
                <p className="text-2xl font-bold">{fuelStats.month_records || 0}</p>
                <p className="text-xs text-slate-500">Total Records</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <Droplets className="w-6 h-6 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{(fuelStats.month_litres || 0).toLocaleString()} L</p>
                <p className="text-xs text-slate-500">Total Litres</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <TrendingUp className="w-6 h-6 mx-auto text-emerald-500 mb-2" />
                <p className="text-2xl font-bold">{(fuelStats.month_km || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-500">Total KM</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <BarChart3 className="w-6 h-6 mx-auto text-purple-500 mb-2" />
                <p className={`text-2xl font-bold ${(fuelStats.avg_consumption || 0) >= 2.2 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {fuelStats.avg_consumption || 0} KM/L
                </p>
                <p className="text-xs text-slate-500">Avg Consumption</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <Truck className="w-6 h-6 mx-auto text-slate-500 mb-2" />
                <p className="text-2xl font-bold">{fuelStats.unique_vehicles || 0}</p>
                <p className="text-xs text-slate-500">Vehicles</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Planning Capture Banner */}
      {(isSupervisor || isManagement) && showPlanningBanner && planningStats && planningStats.today_records === 0 && (
        <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-lg p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <p className="font-semibold text-lg">Daily Planning Capture Required</p>
              <p className="text-sm text-white/80">No load planning records captured for today. Ensure all loads are recorded.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="secondary" 
              size="sm"
              className="bg-white text-teal-600 hover:bg-white/90"
              onClick={() => navigate(`${envPrefix}/planning`)}
            >
              Capture Planning
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={() => setShowPlanningBanner(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Monthly Planning & Loading Statistics Widget */}
      {(isSupervisor || isManagement) && planningStats && (
        <Card className="border-l-4 border-l-teal-500">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-teal-500" />
                Planning & Loading (This Month)
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(`${envPrefix}/planning`)}
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <Package className="w-6 h-6 mx-auto text-teal-500 mb-2" />
                <p className="text-2xl font-bold">{planningStats.total_records || 0}</p>
                <p className="text-xs text-slate-500">Total Loads</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <Gauge className="w-6 h-6 mx-auto text-purple-500 mb-2" />
                <p className={`text-2xl font-bold ${(planningStats.avg_utilization || 0) > 100 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {(planningStats.avg_utilization || 0).toFixed(1)}%
                </p>
                <p className="text-xs text-slate-500">Avg Utilization</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <Scale className="w-6 h-6 mx-auto text-teal-500 mb-2" />
                <p className={`text-2xl font-bold ${(planningStats.avg_over_under || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {(planningStats.avg_over_under || 0) > 0 ? '+' : ''}{(planningStats.avg_over_under || 0).toFixed(1)}%
                </p>
                <p className="text-xs text-slate-500">Avg Over/Under</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <AlertTriangle className="w-6 h-6 mx-auto text-red-500 mb-2" />
                <p className="text-2xl font-bold text-red-600">{planningStats.overloaded_count || 0}</p>
                <p className="text-xs text-slate-500">Overloaded</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <AlertCircle className="w-6 h-6 mx-auto text-orange-500 mb-2" />
                <p className="text-2xl font-bold text-orange-600">{planningStats.with_errors || 0}</p>
                <p className="text-xs text-slate-500">With Errors</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Replacement Plan Widget */}
      {(isSupervisor || isManagement) && replacementStats && (
        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Replace className="w-5 h-5 text-orange-500" />
                Replacement Plan Overview
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(`${envPrefix}/replacement-plan`)}
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <Truck className="w-6 h-6 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{replacementStats.total_vehicles || 0}</p>
                <p className="text-xs text-slate-500">Total Trucks</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <Truck className="w-6 h-6 mx-auto text-purple-500 mb-2" />
                <p className="text-2xl font-bold">{replacementStats.total_trailers || 0}</p>
                <p className="text-xs text-slate-500">Total Trailers</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <AlertTriangle className="w-6 h-6 mx-auto text-red-500 mb-2" />
                <p className="text-2xl font-bold text-red-600">{(replacementStats.trucks_critical || 0) + (replacementStats.trailers_critical || 0)}</p>
                <p className="text-xs text-slate-500">Critical Review</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <AlertCircle className="w-6 h-6 mx-auto text-amber-500 mb-2" />
                <p className="text-2xl font-bold text-amber-600">{(replacementStats.trucks_warning || 0) + (replacementStats.trailers_warning || 0)}</p>
                <p className="text-xs text-slate-500">Warning</p>
              </div>
            </div>
            {/* Items needing review */}
            {replacementStats.needs_review && replacementStats.needs_review.length > 0 && (
              <div className="border-t pt-3">
                <p className="text-sm font-medium text-slate-700 mb-2">Vehicles Requiring Review:</p>
                <div className="space-y-1">
                  {replacementStats.needs_review.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-red-50 px-3 py-2 rounded">
                      <span className="font-mono font-medium">{item.registration}</span>
                      <span className="text-slate-600">{item.type}</span>
                      <span className="text-red-600 text-xs">{item.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="dashboard-grid">
        {isDriver && (
          <>
            <StatCard icon={Clock} label="Due Today" value={stats.due_today || 0} color="orange" />
            <StatCard icon={AlertTriangle} label="Overdue" value={stats.overdue || 0} color="red" />
            <StatCard icon={CheckCircle2} label="Completed This Week" value={stats.completed_this_week || 0} color="emerald" />
            <StatCard icon={TrendingUp} label="Compliance Rate" value={`${stats.compliance_rate || 0}%`} color="blue" />
          </>
        )}
        {isSupervisor && !isManagement && (
          <>
            <StatCard icon={ClipboardList} label="Pending Approvals" value={stats.pending_approval || 0} color="orange" />
            <StatCard icon={AlertTriangle} label="Overdue" value={stats.overdue || 0} color="red" />
            <StatCard icon={Users} label="Team Members" value={stats.team_size || 0} color="blue" />
            <StatCard icon={TrendingUp} label="Team Compliance" value={`${stats.compliance_rate || 0}%`} color="emerald" />
          </>
        )}
        {isManagement && (
          <>
            <StatCard icon={CheckCircle2} label="Approved Submissions" value={stats.approved_submissions || 0} color="emerald" />
            <StatCard icon={Clock} label="Pending Review" value={stats.pending_submissions || 0} color="orange" />
            <StatCard icon={AlertTriangle} label="Open Actions" value={stats.open_corrective_actions || 0} color="red" />
            <StatCard icon={TrendingUp} label="Audit Score" value={`${stats.audit_readiness_score || 0}%`} color="blue" />
          </>
        )}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle 
            className="text-lg uppercase tracking-wide"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="quick-action-grid">
            <QuickActionButton 
              icon={ClipboardList} 
              label="Daily Inspection" 
              onClick={() => handleNewSubmission('F004')}
            />
            <QuickActionButton 
              icon={FileText} 
              label="Fuel Report" 
              onClick={() => handleNewSubmission('F010')}
            />
            <QuickActionButton 
              icon={Users} 
              label="Wellness Check" 
              onClick={() => handleNewSubmission('D002')}
            />
            <QuickActionButton 
              icon={Clock} 
              label="Logbook Entry" 
              onClick={() => handleNewSubmission('D003')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Pending Approvals for Supervisors */}
      {isSupervisor && dashboardData?.pending_approvals?.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle 
              className="text-lg uppercase tracking-wide"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Pending Approvals
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(`${envPrefix}/submissions?status=submitted`)}>
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboardData.pending_approvals.slice(0, 5).map((submission) => (
                <div 
                  key={submission.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-sm hover:bg-slate-100 cursor-pointer transition-colors"
                  onClick={() => navigate(`${envPrefix}/submissions/${submission.id}`)}
                  data-testid={`pending-submission-${submission.id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900">{submission.submission_number}</p>
                    <p className="text-sm text-slate-500">
                      {submission.template?.name || submission.template_code}
                      {submission.driver?.user && ` • ${submission.driver.user.first_name} ${submission.driver.user.last_name}`}
                    </p>
                  </div>
                  <Badge className="badge-warning">Pending</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Submissions */}
      {dashboardData?.recent_submissions?.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle 
              className="text-lg uppercase tracking-wide"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Recent Submissions
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(`${envPrefix}/submissions`)}>
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboardData.recent_submissions.slice(0, 5).map((submission) => (
                <div 
                  key={submission.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-sm hover:bg-slate-100 cursor-pointer transition-colors"
                  onClick={() => navigate(`${envPrefix}/submissions/${submission.id}`)}
                  data-testid={`recent-submission-${submission.id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900 font-mono text-sm">{submission.submission_number}</p>
                    <p className="text-sm text-slate-500">{submission.template_code}</p>
                  </div>
                  <Badge className={
                    submission.status === 'approved' ? 'badge-success' :
                    submission.status === 'submitted' ? 'badge-warning' :
                    submission.status === 'rejected' ? 'badge-error' :
                    'badge-neutral'
                  }>
                    {submission.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fleet Expiry Alerts - for supervisors and management */}
      {(isSupervisor || isManagement) && fleetExpiryData && (
        <Card className="border-l-4 border-l-amber-500" data-testid="fleet-expiry-alerts">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle 
              className="text-lg uppercase tracking-wide flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Fleet Expiry Alerts
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(`${envPrefix}/reports`)}>
              View Report
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-red-600">
                  {(fleetExpiryData.license_expiry_counts?.expired || 0) + 
                   (fleetExpiryData.port_permit_expiry_counts?.expired || 0)}
                </p>
                <p className="text-xs text-red-700 font-medium">EXPIRED</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-red-500">
                  {(fleetExpiryData.license_expiry_counts?.['1'] || 0) + 
                   (fleetExpiryData.license_expiry_counts?.['7'] || 0) +
                   (fleetExpiryData.port_permit_expiry_counts?.['1'] || 0) + 
                   (fleetExpiryData.port_permit_expiry_counts?.['7'] || 0)}
                </p>
                <p className="text-xs text-red-600 font-medium">≤7 DAYS</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-orange-600">
                  {(fleetExpiryData.license_expiry_counts?.['14'] || 0) + 
                   (fleetExpiryData.port_permit_expiry_counts?.['14'] || 0)}
                </p>
                <p className="text-xs text-orange-700 font-medium">≤14 DAYS</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">
                  {(fleetExpiryData.license_expiry_counts?.['30'] || 0) + 
                   (fleetExpiryData.port_permit_expiry_counts?.['30'] || 0)}
                </p>
                <p className="text-xs text-amber-700 font-medium">≤30 DAYS</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {(fleetExpiryData.license_expiry_counts?.['60'] || 0) + 
                   (fleetExpiryData.license_expiry_counts?.['90'] || 0) +
                   (fleetExpiryData.port_permit_expiry_counts?.['60'] || 0) +
                   (fleetExpiryData.port_permit_expiry_counts?.['90'] || 0)}
                </p>
                <p className="text-xs text-blue-700 font-medium">≤90 DAYS</p>
              </div>
            </div>

            {/* Expired Items */}
            {(fleetExpiryData.license_expiry?.expired?.length > 0 || 
              fleetExpiryData.port_permit_expiry?.expired?.length > 0) && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="font-semibold text-red-800 text-sm">EXPIRED - Immediate Action Required</span>
                </div>
                <div className="space-y-2">
                  {fleetExpiryData.license_expiry?.expired?.map((v) => (
                    <div key={`lic-exp-${v.id}`} className="flex justify-between items-center text-sm">
                      <span className="font-mono font-medium text-slate-900">{v.registration}</span>
                      <span className="text-red-600">License expired {Math.abs(v.days_remaining)} days ago</span>
                    </div>
                  ))}
                  {fleetExpiryData.port_permit_expiry?.expired?.map((v) => (
                    <div key={`port-exp-${v.id}`} className="flex justify-between items-center text-sm">
                      <span className="font-mono font-medium text-slate-900">{v.registration}</span>
                      <span className="text-red-600">Port Permit expired {Math.abs(v.days_remaining)} days ago</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Critical Items (≤7 days) */}
            {(fleetExpiryData.license_expiry?.['7']?.length > 0 || 
              fleetExpiryData.license_expiry?.['1']?.length > 0 ||
              fleetExpiryData.port_permit_expiry?.['7']?.length > 0 ||
              fleetExpiryData.port_permit_expiry?.['1']?.length > 0) && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="font-semibold text-red-800 text-sm">CRITICAL - Expiring Within 7 Days</span>
                </div>
                <div className="space-y-2">
                  {[...(fleetExpiryData.license_expiry?.['1'] || []), 
                    ...(fleetExpiryData.license_expiry?.['7'] || [])].map((v) => (
                    <div key={`lic7-${v.id}`} className="flex justify-between items-center text-sm">
                      <span className="font-mono font-medium text-slate-900">{v.registration}</span>
                      <span className="text-red-700">License: {v.days_remaining} days</span>
                    </div>
                  ))}
                  {[...(fleetExpiryData.port_permit_expiry?.['1'] || []), 
                    ...(fleetExpiryData.port_permit_expiry?.['7'] || [])].map((v) => (
                    <div key={`port7-${v.id}`} className="flex justify-between items-center text-sm">
                      <span className="font-mono font-medium text-slate-900">{v.registration}</span>
                      <span className="text-red-700">Port Permit: {v.days_remaining} days</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warning Items (≤14 days) */}
            {(fleetExpiryData.license_expiry?.['14']?.length > 0 || 
              fleetExpiryData.port_permit_expiry?.['14']?.length > 0) && (
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="font-semibold text-orange-800 text-sm">WARNING - Expiring Within 14 Days</span>
                </div>
                <div className="space-y-2">
                  {fleetExpiryData.license_expiry?.['14']?.map((v) => (
                    <div key={`lic14-${v.id}`} className="flex justify-between items-center text-sm">
                      <span className="font-mono font-medium text-slate-900">{v.registration}</span>
                      <span className="text-orange-700">License: {v.days_remaining} days</span>
                    </div>
                  ))}
                  {fleetExpiryData.port_permit_expiry?.['14']?.map((v) => (
                    <div key={`port14-${v.id}`} className="flex justify-between items-center text-sm">
                      <span className="font-mono font-medium text-slate-900">{v.registration}</span>
                      <span className="text-orange-700">Port Permit: {v.days_remaining} days</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attention Items (≤30 days) */}
            {(fleetExpiryData.license_expiry?.['30']?.length > 0 || 
              fleetExpiryData.port_permit_expiry?.['30']?.length > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="font-semibold text-amber-800 text-sm">ATTENTION - Expiring Within 30 Days</span>
                </div>
                <div className="space-y-2">
                  {fleetExpiryData.license_expiry?.['30']?.map((v) => (
                    <div key={`lic30-${v.id}`} className="flex justify-between items-center text-sm">
                      <span className="font-mono font-medium text-slate-900">{v.registration}</span>
                      <span className="text-amber-700">License: {v.days_remaining} days</span>
                    </div>
                  ))}
                  {fleetExpiryData.port_permit_expiry?.['30']?.map((v) => (
                    <div key={`port30-${v.id}`} className="flex justify-between items-center text-sm">
                      <span className="font-mono font-medium text-slate-900">{v.registration}</span>
                      <span className="text-amber-700">Port Permit: {v.days_remaining} days</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No alerts message */}
            {fleetExpiryData.total_vehicles > 0 && 
             (fleetExpiryData.license_expiry_counts?.expired || 0) === 0 &&
             (fleetExpiryData.port_permit_expiry_counts?.expired || 0) === 0 &&
             (fleetExpiryData.license_expiry_counts?.['7'] || 0) === 0 &&
             (fleetExpiryData.port_permit_expiry_counts?.['7'] || 0) === 0 &&
             (fleetExpiryData.license_expiry_counts?.['14'] || 0) === 0 &&
             (fleetExpiryData.port_permit_expiry_counts?.['14'] || 0) === 0 &&
             (fleetExpiryData.license_expiry_counts?.['30'] || 0) === 0 &&
             (fleetExpiryData.port_permit_expiry_counts?.['30'] || 0) === 0 && (
              <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-sm">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">All fleet licenses and permits are current (beyond 30 days)</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Inspection Issues Pending Action - for supervisors and management */}
      {(isSupervisor || isManagement) && inspectionIssues && inspectionIssues.total_open_issues > 0 && (
        <Card className="border-l-4 border-l-orange-500" data-testid="inspection-issues">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle 
              className="text-lg uppercase tracking-wide flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Inspection Issues Pending Action
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(`${envPrefix}/corrective-actions`)}>
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-orange-600">{inspectionIssues.total_open_issues}</p>
                <p className="text-xs text-orange-700 font-medium">OPEN ISSUES</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-slate-600">{inspectionIssues.vehicles_affected}</p>
                <p className="text-xs text-slate-700 font-medium">VEHICLES AFFECTED</p>
              </div>
            </div>

            {/* Issues by Vehicle */}
            <div className="space-y-3">
              {inspectionIssues.issues_by_vehicle?.slice(0, 5).map((vehicle) => (
                <div key={vehicle.registration} className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-mono font-bold text-slate-900">{vehicle.registration}</span>
                      {vehicle.driver_name && (
                        <span className="text-sm text-slate-500 ml-2">({vehicle.driver_name})</span>
                      )}
                    </div>
                    <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                      {vehicle.issues.length} issue{vehicle.issues.length > 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    {vehicle.issues.slice(0, 3).map((issue) => (
                      <div key={issue.id} className="text-sm text-slate-600 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                        {issue.title.replace('Inspection Issue: ', '').replace(` - ${vehicle.registration}`, '')}
                      </div>
                    ))}
                    {vehicle.issues.length > 3 && (
                      <div className="text-sm text-slate-500 italic">
                        +{vehicle.issues.length - 3} more issue{vehicle.issues.length - 3 > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Service Schedule Alerts - for supervisors and management */}
      {(isSupervisor || isManagement) && serviceAlerts && (
        <Card className="border-l-4 border-l-blue-500" data-testid="service-alerts">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle 
              className="text-lg uppercase tracking-wide flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              <Wrench className="w-5 h-5 text-blue-500" />
              Service Schedule Alerts
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(`${envPrefix}/service-schedule`)}>
              Manage
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-red-600">
                  {(serviceAlerts.vehicles?.critical_count || 0) + 
                   (serviceAlerts.trailers?.critical_count || 0) + 
                   (serviceAlerts.fire_extinguishers?.critical_count || 0)}
                </p>
                <p className="text-xs text-red-700 font-medium">CRITICAL</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-orange-600">
                  {(serviceAlerts.vehicles?.warning_count || 0) + 
                   (serviceAlerts.trailers?.warning_count || 0) + 
                   (serviceAlerts.fire_extinguishers?.warning_count || 0)}
                </p>
                <p className="text-xs text-orange-700 font-medium">WARNING</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">
                  {(serviceAlerts.vehicles?.attention_count || 0) + 
                   (serviceAlerts.trailers?.attention_count || 0) + 
                   (serviceAlerts.fire_extinguishers?.attention_count || 0)}
                </p>
                <p className="text-xs text-amber-700 font-medium">ATTENTION</p>
              </div>
            </div>

            {/* Critical Items */}
            {(serviceAlerts.vehicles?.alerts?.critical?.length > 0 || 
              serviceAlerts.trailers?.alerts?.critical?.length > 0 || 
              serviceAlerts.fire_extinguishers?.alerts?.critical?.length > 0) && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="font-semibold text-red-800 text-sm">CRITICAL - Immediate Action Required</span>
                </div>
                <div className="space-y-2">
                  {serviceAlerts.vehicles?.alerts?.critical?.slice(0, 3).map((v) => (
                    <div key={v.id} className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-slate-500" />
                        <span className="font-mono font-medium text-slate-900">{v.registration}</span>
                      </div>
                      <span className="text-red-600">
                        {v.km_remaining !== null && v.km_remaining <= 0 
                          ? `Overdue by ${Math.abs(v.km_remaining)} km` 
                          : v.km_remaining !== null 
                            ? `${v.km_remaining} km remaining`
                            : v.days_remaining !== null && v.days_remaining <= 0 
                              ? `Overdue by ${Math.abs(v.days_remaining)} days`
                              : `${v.days_remaining} days`}
                      </span>
                    </div>
                  ))}
                  {serviceAlerts.fire_extinguishers?.alerts?.critical?.slice(0, 2).map((e) => (
                    <div key={e.id} className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Gauge className="w-4 h-4 text-slate-500" />
                        <span className="font-mono font-medium text-slate-900">{e.serial_number}</span>
                        <span className="text-slate-500 text-xs">on {e.entity_registration}</span>
                      </div>
                      <span className="text-red-600">
                        {e.days_remaining <= 0 ? `Overdue by ${Math.abs(e.days_remaining)} days` : `${e.days_remaining} days`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No alerts message */}
            {(serviceAlerts.vehicles?.critical_count || 0) === 0 &&
             (serviceAlerts.vehicles?.warning_count || 0) === 0 &&
             (serviceAlerts.vehicles?.attention_count || 0) === 0 &&
             (serviceAlerts.trailers?.critical_count || 0) === 0 &&
             (serviceAlerts.fire_extinguishers?.critical_count || 0) === 0 && (
              <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-sm">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">All vehicle services are up to date</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tyre Survey Progress Widget - for supervisors and management */}
      {(isSupervisor || isManagement) && tyreSurveyStatus && (
        <Card className="border-l-4 border-l-indigo-500" data-testid="tyre-survey-widget">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle 
              className="text-lg uppercase tracking-wide flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              <CircleDot className="w-5 h-5 text-indigo-500" />
              Tyre Survey Progress
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(`${envPrefix}/tyre-survey`)}>
              Manage
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress Display */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">
                  {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {tyreSurveyStatus.surveyed_tyres} of {tyreSurveyStatus.total_tyres} running tyres surveyed
                </p>
              </div>
              <div className="text-right">
                <div className={`text-4xl font-bold font-mono ${
                  tyreSurveyStatus.percentage >= 75 ? 'text-emerald-600' :
                  tyreSurveyStatus.percentage >= 50 ? 'text-amber-600' :
                  'text-red-600'
                }`}>
                  {tyreSurveyStatus.percentage}%
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${
                  tyreSurveyStatus.percentage >= 75 ? 'bg-emerald-500' :
                  tyreSurveyStatus.percentage >= 50 ? 'bg-amber-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${tyreSurveyStatus.percentage}%` }}
              />
            </div>

            {/* Pending Setups */}
            {tyreSurveyStatus.pending_setups?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="font-semibold text-amber-800 text-sm">
                    {tyreSurveyStatus.pending_setups.length} Setup(s) Pending Survey
                  </span>
                </div>
                <div className="space-y-1">
                  {tyreSurveyStatus.pending_setups.slice(0, 3).map((setup) => (
                    <div key={setup.id} className="flex justify-between items-center text-sm">
                      <span className="font-mono font-medium text-slate-900">
                        {setup.truck_registration}
                        {setup.trailer_registration && ` + ${setup.trailer_registration}`}
                      </span>
                      <span className="text-amber-700">{setup.tyre_count} tyres</span>
                    </div>
                  ))}
                  {tyreSurveyStatus.pending_setups.length > 3 && (
                    <div className="text-sm text-slate-500 italic">
                      +{tyreSurveyStatus.pending_setups.length - 3} more...
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* All Complete */}
            {tyreSurveyStatus.total_setups > 0 && tyreSurveyStatus.percentage === 100 && (
              <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-sm">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">All tyre surveys completed for this month!</span>
              </div>
            )}

            {/* No setups configured - ACTION REQUIRED */}
            {tyreSurveyStatus.total_setups === 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="font-semibold text-red-800 text-sm">
                      Action Required: Configure Monthly Setups
                    </span>
                  </div>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => navigate(`${envPrefix}/tyre-survey`)}
                  >
                    Configure Now
                  </Button>
                </div>
                <p className="text-xs text-red-700 mt-2">
                  No tyre survey setups have been configured for this month. Create setups to track which trucks/trailers need inspection.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
